import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchDrafts,
  fetchDraftDetail,
  saveDraft,
  confirmDraft,
  askMoreDraft,
  rejectDraft,
  generateDraft,
  fetchAssignmentCandidates,
  fetchTriggerContext,
  createZentaoTask,
} from '@/services/api/drafts'

export function useDraftList(params: {
  page?: number
  page_size?: number
  status?: string
  keyword?: string
  sort_by?: string
  sort_order?: string
}) {
  return useQuery({
    queryKey: ['drafts', 'list', params],
    queryFn: () => fetchDrafts(params),
    staleTime: 15_000,
  })
}

export function useDraftDetail(draftId: string | null) {
  return useQuery({
    queryKey: ['drafts', 'detail', draftId],
    queryFn: () => fetchDraftDetail(draftId!),
    enabled: !!draftId,
  })
}

export function useSaveDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, payload }: { draftId: string; payload: Record<string, unknown> }) =>
      saveDraft(draftId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
  })
}

export function useConfirmDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, payload }: { draftId: string; payload: Record<string, unknown> }) =>
      confirmDraft(draftId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useAskMoreDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, payload }: { draftId: string; payload: Record<string, unknown> }) =>
      askMoreDraft(draftId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
    },
  })
}

export function useRejectDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, payload }: { draftId: string; payload: Record<string, unknown> }) =>
      rejectDraft(draftId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useGenerateDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (messageId: string) => generateDraft(messageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
      qc.invalidateQueries({ queryKey: ['messages'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

/** 获取研发分配候选池 */
export function useAssignmentCandidates(draftId: string | null) {
  return useQuery({
    queryKey: ['drafts', 'assignment-candidates', draftId],
    queryFn: () => fetchAssignmentCandidates(draftId!),
    enabled: !!draftId,
  })
}

/** 获取触发消息上下文 */
export function useTriggerContext(draftId: string | null) {
  return useQuery({
    queryKey: ['drafts', 'trigger-context', draftId],
    queryFn: () => fetchTriggerContext(draftId!),
    enabled: !!draftId,
  })
}

/** 创建禅道 task */
export function useCreateZentaoTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, payload }: { draftId: string; payload: Record<string, unknown> }) =>
      createZentaoTask(draftId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drafts'] })
      qc.invalidateQueries({ queryKey: ['tickets'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}
